import { Head, Link } from "@inertiajs/react"
import { Fragment, useMemo } from "react"
import { Printer } from "lucide-react"

import { AppShell } from "@/components/AppShell"
import { type HeaderPair, HeaderColumn } from "@/components/print/PrintSheetHeader"
import { Button } from "@/components/ui/button"
import { roomFigures, scheduleAddOnsByRow, surfaceFigures } from "@/editor-core/addons"
import {
  type Subfloor,
  type WireAddOn,
  type WireProductAddOn,
  type WireRoom,
  type WireSurface,
  addOnsFromServer,
  roomsFromServer,
  rulesFromServer,
  surfacesFromServer,
} from "@/editor-core/document"
import {
  EM_DASH,
  addOnMaterialLabel,
  colorLabel,
  conditionLabel,
  directionLabel,
  groutLabel,
  materialLabel,
  notesLine,
  patternLabel,
  styleLabel,
  subfloorLabel,
} from "@/editor-core/labels"
import { type PlacedImage, resolveSubfloor, scheduleRows } from "@/editor-core/schedule"

// The material schedule — the document installers and the field team read
// alongside the takeoff diagram.
//
// It deliberately carries NO numbers: no square footage, no order quantities,
// no prices. It is a specification and instruction sheet. "How much" is the
// worksheet's question and "what does it cost" is the bid's; collapsing them
// into one grid is exactly the over-complication this replaces.
//
// Every displayed value goes through editor-core/labels, so a raw enum can
// never reach a page a customer's installer reads.

type Props = {
  takeoff: {
    id: number
    name: string
    url: string
    owner: { kind: "plan" | "project"; id: number; display_name: string; url: string }
  }
  header: {
    title: string
    subtitle: string | null
    left: HeaderPair[]
    right: HeaderPair[]
  }
  document: { rooms: WireRoom[]; surfaces: WireSurface[]; add_ons: WireAddOn[] }
  product_add_ons: WireProductAddOn[]
  sheet_images: Record<number, PlacedImage[]>
  sheets: { id: number; label: string }[]
  project_subfloor: Subfloor
  comments: string | null
}

const COLUMNS = [
  "Room / Surface",
  "Material",
  "Style",
  "Color",
  "Grout",
  "Pattern",
  "Direction",
  "Subfloor",
  "Notes",
]

export default function SchedulePage(props: Props) {
  const { takeoff, header, comments } = props

  const { rows, addOnLines, sheetAddOnBlocks } = useMemo(() => {
    const rooms = roomsFromServer(props.document.rooms)
    const surfaces = surfacesFromServer(props.document.surfaces)
    const rows = scheduleRows(rooms, surfaces).map((row) => {
      if (row.kind === "room") {
        return {
          id: row.id,
          indented: false,
          name: row.room.name,
          product: row.room.product,
          grout: row.room.grout,
          notes: row.room.notes,
          // A surface has no geometry to hit-test, so only rooms consult the
          // sheet-image tier of the cascade.
          subfloor: resolveSubfloor(
            row.room,
            props.sheet_images[row.room.sheetId] ?? [],
            props.project_subfloor
          ),
        }
      }
      return {
        id: row.id,
        indented: row.attached,
        name: row.surface.name,
        product: row.surface.product,
        grout: row.surface.grout,
        notes: row.surface.notes,
        subfloor: row.surface.subfloor ?? props.project_subfloor,
      }
    })

    // Add-on lines print under their row — the material that used to hide as
    // note prose, now real rows. Same gating the worksheet uses (print flag +
    // per-row subfloor condition), same modules, no way to disagree. No
    // calibration ships here, and none is needed: the schedule carries no
    // quantities, and the gates only read product ids and subfloors.
    const figures = rooms.map((room) =>
      roomFigures(room, null, props.sheet_images[room.sheetId] ?? [], props.project_subfloor)
    )
    const sFigures = surfaces.map((surface) => surfaceFigures(surface, props.project_subfloor))
    const addOnLines = scheduleAddOnsByRow(
      addOnsFromServer(props.document.add_ons),
      rulesFromServer(props.product_add_ons),
      figures,
      sFigures
    )
    const sheetAddOnBlocks = props.sheets
      .map((sheet) => ({ sheet, lines: addOnLines.get(`sheet:${sheet.id}`) ?? [] }))
      .filter((block) => block.lines.length > 0)

    return { rows, addOnLines, sheetAddOnBlocks }
  }, [props.document, props.product_add_ons, props.sheets, props.sheet_images, props.project_subfloor])

  const title = `Material Schedule — ${header.title}`
  const description =
    "What material goes on each room and surface, laid which way, with which grout and subfloor — the spec sheet installers work from."

  return (
    <>
      <Head title={title}>
        <meta name="description" content={description} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
      </Head>
      <AppShell>
        <div className="print-chrome mb-4 flex flex-wrap items-center gap-3">
          <Link href={takeoff.url} className="no-underline hover:underline">
            ← {takeoff.name}
          </Link>
          <span className="ml-auto flex gap-2">
            <Button variant="soft" onClick={() => window.print()} data-testid="print-schedule">
              <Printer className="mr-1.5 h-4 w-4" />
              Print / Save as PDF
            </Button>
          </span>
        </div>

        <article className="print-sheet mx-auto max-w-5xl rounded-lg border border-hairline bg-surface p-6">
          <header className="flex flex-wrap items-baseline justify-between gap-2 border-b border-hairline pb-3">
            <h1>Material Schedule</h1>
            <p className="text-ink-display">
              {header.title}
              {header.subtitle && <span className="text-ink-muted"> · {header.subtitle}</span>}
            </p>
          </header>

          <div className="mt-4 grid gap-x-8 gap-y-1 sm:grid-cols-2">
            <HeaderColumn pairs={header.left} />
            <HeaderColumn pairs={header.right} />
          </div>

          <div className="mt-6 overflow-x-auto">
            <table className="w-full border-collapse text-sm" data-testid="schedule-table">
              <thead>
                <tr className="border-b border-ink-muted text-left align-bottom text-xs uppercase tracking-wide text-ink-muted">
                  {COLUMNS.map((column) => (
                    <th key={column} className="px-1.5 pb-1 font-semibold">
                      {column}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 ? (
                  <tr>
                    <td colSpan={COLUMNS.length} className="px-1.5 py-4 text-ink-muted">
                      Nothing on this takeoff yet — trace a room or add a surface in the editor.
                    </td>
                  </tr>
                ) : (
                  rows.map((row) => (
                    <Fragment key={row.id}>
                      <tr
                        data-testid={`schedule-row-${row.id}`}
                        className="schedule-row border-b border-hairline align-top"
                      >
                        <td className={`px-1.5 py-2 font-medium ${row.indented ? "pl-4" : ""}`}>
                          {row.name}
                        </td>
                        <td className="px-1.5 py-2">{materialLabel(row.product)}</td>
                        <td className="px-1.5 py-2">{styleLabel(row.product)}</td>
                        <td className="px-1.5 py-2">{colorLabel(row.product)}</td>
                        <td className="px-1.5 py-2">{groutLabel(row.grout)}</td>
                        <td className="px-1.5 py-2">{patternLabel(row.product?.pattern)}</td>
                        <td className="px-1.5 py-2">
                          {directionLabel(row.product?.layout_angle, row.product?.pattern)}
                        </td>
                        <td className="px-1.5 py-2">{subfloorLabel(row.subfloor)}</td>
                        <td className="px-1.5 py-2">{notesLine(row.notes)}</td>
                      </tr>
                      {(addOnLines.get(row.id) ?? []).map((line) => (
                        <tr
                          key={line.key}
                          data-testid={`schedule-addon-${line.key}`}
                          className="schedule-row border-b border-hairline align-top"
                        >
                          <td
                            className={`px-1.5 py-1.5 italic ${row.indented ? "pl-6" : "pl-4"}`}
                          >
                            {line.label}
                          </td>
                          <td className="px-1.5 py-1.5 italic">{addOnMaterialLabel(line.item)}</td>
                          <td className="px-1.5 py-1.5">{EM_DASH}</td>
                          <td className="px-1.5 py-1.5">{EM_DASH}</td>
                          <td className="px-1.5 py-1.5">{EM_DASH}</td>
                          <td className="px-1.5 py-1.5">{EM_DASH}</td>
                          <td className="px-1.5 py-1.5">{EM_DASH}</td>
                          <td className="px-1.5 py-1.5">{EM_DASH}</td>
                          <td className="px-1.5 py-1.5 italic">
                            {conditionLabel(line.condition) ?? EM_DASH}
                          </td>
                        </tr>
                      ))}
                    </Fragment>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {sheetAddOnBlocks.length > 0 && (
            <section className="mt-6 border-t border-hairline pt-3" data-testid="sheet-addon-block">
              <h2>Sheet-wide</h2>
              {sheetAddOnBlocks.map(({ sheet, lines }) => (
                <p key={sheet.id} className="mt-1">
                  <span className="text-ink-muted">{sheet.label}:</span>{" "}
                  {lines.map((line) => line.label).join(" · ")}
                </p>
              ))}
            </section>
          )}

          {comments && (
            <section className="mt-6 border-t border-hairline pt-3">
              <h2>Comments</h2>
              <p className="whitespace-pre-wrap">{comments}</p>
            </section>
          )}

          <footer className="mt-6 border-t border-hairline pt-2 text-xs text-ink-muted">
            {header.title}
            {header.subtitle && ` · ${header.subtitle}`} · {takeoff.name}
          </footer>
        </article>
      </AppShell>
    </>
  )
}

